<!--
 * COPYRIGHT Transtron Inc 2023
 *
 * 機能名：会社情報一覧画面
 *
 * 作成者：2023/06/19 ノア）馬
 * 最終修正者：2023/06/19 ノア）馬
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('gpsTracker.companyInfo.title') }}</span>
          </div>
        </div>
        <div class="pull-right">
          <div class="searchBox">
            <el-input
              v-model.trim="searchText"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.companyInfo.textFieldSearch')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            ></el-input>
            <!-- 検索 -->
            <el-button
              class="tti-btn-default tti-btn-002"
              @click="onSearchBoxClick"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnSearch')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              {{ $t('buttons.search') }}
            </el-button>
            <!-- 全表示 -->
            <el-button
              class="tti-btn-default tti-btn-002"
              @click="onAllDisplayClick"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnViewAll')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              {{ $t('buttons.allDisplay') }}
            </el-button>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <el-table
        ref="tableRef"
        :data="tableData"
        height="calc(100vh - 180px)"
        :border="true"
        :stripe="true"
        header-row-class-name="tti-master-table-header"
        :empty-text="$t('messages.common.emptyTableData')"
        highlight-current-row
        @current-change="handleCurrentChange"
      >
        <!-- 会社名 -->
        <el-table-column prop="companyName" :label="$t('gpsTracker.companyInfo.companyName')" width="230">
        </el-table-column>
        <!-- 会社コード -->
        <el-table-column prop="companyCode" :label="$t('gpsTracker.companyInfo.companyCode')" width="160">
        </el-table-column>
        <!-- Prefix -->
        <el-table-column prop="companyPrefix" :label="$t('gpsTracker.companyInfo.companyPrefix')" width="160">
        </el-table-column>
        <!-- ID上限数 -->
        <el-table-column prop="idUpperLimit" :label="$t('gpsTracker.companyInfo.idUpperLimit')" width="80">
        </el-table-column>
        <!-- 端末上限数 -->
        <el-table-column prop="terminalUpperLimit" :label="$t('gpsTracker.companyInfo.terminalUpperLimit')" width="100">
        </el-table-column>
        <!-- 管理対象 -->
        <el-table-column
          prop="managementTargetShowName"
          :label="$t('gpsTracker.companyInfo.managementTargetShowName')"
          width="200"
        >
        </el-table-column>
        <!-- 電話番号 -->
        <el-table-column prop="tel" :label="$t('gpsTracker.companyInfo.tel')" width="160"> </el-table-column>
        <!-- メールアドレス -->
        <el-table-column prop="mail" :label="$t('gpsTracker.companyInfo.mail')" width="300"> </el-table-column>
      </el-table>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 新規登録 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onNewRegistClick"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnAddCompany')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.newRegist') }}
        </el-button>
        <!-- 編集 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onEditClick"
          :disabled="isBTNDisabled"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnEditCompany')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.edit') }}
        </el-button>
        <!-- オプション機能設定 -->
        <el-button
          class="tti-btn-default tti-btn-006"
          @click="onOpSetClick"
          :disabled="isBTNDisabled"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnOpSetCompany')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.opSet') }}
        </el-button>
        <!-- 会社削除 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onDeleteClick"
          :disabled="!isEnableDelete"
          v-tooltip="getTooltip(tooltipType.delete)"
        >
          {{ $t('buttons.companyDelete') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 端末情報検索 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onSigfoxSearchClick"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnSigfoxSearch')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.sigfoxSearch') }}
        </el-button>
        <!-- データ復旧 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onDataRecoveryClick"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnDataRecovery')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.dataRecovery') }}
        </el-button>
        <!-- 会社登録ユーザ一覧 -->
        <el-button
          class="tti-btn-default tti-btn-006"
          @click="onManagementRegisterClick"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnManagementRegister')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.managementRegister') }}
        </el-button>
        <!-- 管理対象一覧 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onManagementTargetClick"
          :disabled="isBTNDisabled"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnManagementTargetList')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.managementTargetDisplay') }}
        </el-button>
        <!-- ユーザ登録 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onUserRegistClick"
          :disabled="isBTNDisabled"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnUserList')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.userRegist') }}
        </el-button>
        <!-- お知らせ -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onAnnouncementsClick"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnNews')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.notice') }}
        </el-button>
        <!-- 取込 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="uptakeClick"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.companyInfo.btnCsvIn')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.capture') }}
        </el-button>

        <!-- file -->
        <input ref="inputFile" type="file" @change="onFileChange" class="fileInput" accept=".csv" />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Table } from 'element-ui'

import TOOL_COMPANY_BASE from '@/views/companyEditTool/TOOL_COMPANY_BASE.vue'
import { i18n } from '@/locales'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { getToolCompanyInfoData, deleteCompanyInfo, uploadToolCSV } from '@/store/apis'
import { CompanyAddToolData, ToolCompanyMasterData, ToolCompanyDetailDeleteParam } from '@/models'
import config from '@/config/config'

@Component({
  name: 'TOOL_COMPANY_MASTER'
})
export default class TOOL_COMPANY_MASTER extends TOOL_COMPANY_BASE {
  @Ref('inputFile')
  $inputFile!: HTMLInputElement

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'TOOL_COMPANY_MASTER'

  /**
   * 検索文字列
   */
  searchText = ''

  /**
   * 変更したデータ
   */
  tableData: Array<ToolCompanyMasterData> = []

  /**
   * テーブル行で選択したデータ
   */
  singleSelection: ToolCompanyMasterData | undefined

  /**
   * ボタンのアクティブ状態
   */
  isBTNDisabled = true

  /**
   * Shiftキー押下フラグ
   */
  shiftKeyDown = false

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('TOOL_COMPANY_MASTER > mounted')

    this.fetchTableData()

    document.addEventListener('keydown', this.watchKeydown)
    document.addEventListener('keyup', this.watchKeyup)
  }

  /**
   * 待ち受けキーボード押下イベント
   *
   * @param {KeyboardEvent} e キーボードイベント
   */
  watchKeydown(e: KeyboardEvent): void {
    if (e && e.shiftKey) {
      this.shiftKeyDown = true
    }
  }

  /**
   * リスニングキーボード・キーのリフト・イベント
   *
   * @param {KeyboardEvent} e キーボードイベント
   */
  watchKeyup(e: KeyboardEvent): void {
    if (e && !e.shiftKey) {
      this.shiftKeyDown = false
    }
  }

  /**
   * 破棄がフック処理
   *
   * @returns {void}
   */
  destroyed(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > destroyed')
    document.removeEventListener('keydown', this.watchKeydown)
    document.removeEventListener('keyup', this.watchKeyup)
  }

  fetchTableDataBefore(): () => Promise<CompanyAddToolData> {
    return () => {
      const param = {
        nameOrCode: this.searchText
      }
      return getToolCompanyInfoData(param)
    }
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      this.$log.debug(`TOOL_COMPANY_MASTER > fetchTableData`)
      const data = (await this.fetchTableDataBefore()()) as CompanyAddToolData
      this.initTableData(data.list as Array<ToolCompanyMasterData>)
      return true
    } catch (e) {
      this.initTableData([])
      this.$log.debug(`TOOL_COMPANY_MASTER > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {Array<any>} data 検索されたデータ
   * @returns {void}
   */
  initTableData(data: Array<ToolCompanyMasterData>): void {
    if (!data) {
      data = []
    }
    this.tableData = data

    this.singleSelection = {} as ToolCompanyMasterData
    this.isBTNDisabled = true
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @returns {void}
   */
  onSearchBoxClick(): void {
    this.searchText = this.searchText.replace(/[,]/g, '')
    if (!this.searchText) {
      this.searchText = ''
    }
    this.fetchTableData()
  }

  /**
   * テーブルの選択したデータを変更する。
   *
   * @param {ToolCompanyMasterData} row 選択されたデータ
   */
  handleCurrentChange(row: ToolCompanyMasterData): void {
    this.$log.debug(`TOOL_COMPANY_MASTER > ${this.pageId} > handleCurrentChange,`, row)
    if (row) {
      this.singleSelection = row
      this.isBTNDisabled = false
    } else {
      this.isBTNDisabled = true
    }
  }

  /**
   * 全表示
   *
   */
  onAllDisplayClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onAllDisplayClick ')
    this.searchText = ''
    this.fetchTableData()
  }

  /**
   * 新規登録
   *
   */
  onNewRegistClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onNewRegistClick ')
    this.$router.push(config.toolMenu.companyInfoAddEdit)
  }

  /**
   * 編集
   *
   */
  onEditClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onEditClick ')
    if (this.singleSelection) {
      this.$router.push({
        path: config.toolMenu.companyInfoAddEdit,
        query: {
          companyId: this.singleSelection.id
        }
      })
    }
  }

  /**
   * オプション機能設定
   *
   */
  onOpSetClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onOpSetClick ')
    if (this.singleSelection) {
      this.$router.push({
        path: config.toolMenu.companyInfoOpSet,
        query: {
          companyId: this.singleSelection.id
        }
      })
    }
  }

  /**
   * 会社削除ボタンのアクティブ状態の判断
   *
   * @returns {boolean} 会社削除ボタンのアクティブ状態
   */
  get isEnableDelete(): boolean {
    return !this.isBTNDisabled && this.shiftKeyDown
  }

  /**
   * 会社削除
   *
   */
  onDeleteClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onDeleteClick ')
    const confirmCallback = () => {
      this.doDeleteCompanyInfo()
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.deleteConfirm').toString(), confirmCallback)
  }

  /**
   * 会社削除実行
   */
  async doDeleteCompanyInfo(): Promise<void> {
    if (this.singleSelection) {
      const param = {
        id: this.singleSelection.id
      } as ToolCompanyDetailDeleteParam
      await deleteCompanyInfo(param)
      msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString(), () => {
        this.fetchTableData()
      })
    }
  }

  /**
   * 管理対象一覧
   *
   */
  onManagementTargetClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onManagementTargetClick ')
    if (this.singleSelection) {
      this.$router.push({
        path: config.toolMenu.companyManagementTarget,
        query: {
          companyId: this.singleSelection.id
        }
      })
    }
  }

  /**
   * ユーザ登録
   *
   */
  onUserRegistClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onUserRegistClick ')
    if (this.singleSelection) {
      this.$router.push({
        path: config.toolMenu.userInfo,
        query: {
          companyId: this.singleSelection.id,
          companyPrefix: this.singleSelection.companyPrefix
        }
      })
    }
  }

  /**
   * お知らせ
   *
   */
  onAnnouncementsClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onAnnouncementsClick ')
    this.$router.push(config.toolMenu.announcementInfo)
  }

  /**
   * 管理登録
   */
  onManagementRegisterClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onManagementRegisterClick ')
    this.$router.push({
      path: config.toolMenu.companyManagementRegister
    })
  }

  /**
   * 端末情報検索
   */
  onSigfoxSearchClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onSigfoxSearchClick ')
    this.$router.push(config.toolMenu.companySigfoxInfo)
  }

  /**
   * データ復旧
   */
  onDataRecoveryClick(): void {
    this.$log.debug('TOOL_COMPANY_MASTER > onDataRecoveryClick ')
    this.$router.push(config.toolMenu.companyDataRecovery)
  }

  /**
   * 取込
   *
   */
  async uptakeClick(): Promise<void> {
    this.$log.debug('TOOL_COMPANY_MASTER > uptakeClick ')
    this.handleInputFile()
  }

  /**
   * 入力ファイル処理
   *
   * @returns {void}
   */
  handleInputFile(): void {
    this.$inputFile.value = ''
    this.$inputFile.click()
  }

  /**
   * 選択したファイルを変更した時の処理。
   *
   * @param {Event} event イベント
   * @returns {void}
   */
  async onFileChange(event: Event): Promise<void> {
    this.$log.debug(`TOOL_COMPANY_MASTER > onFileChange: event=`, event)
    const files = (event.target as HTMLInputElement).files
    if (!files || files.length === 0) {
      return
    }

    const file = files[0]

    if (!file) {
      return
    }

    if (!this.inputFileCheck(file)) {
      return
    }

    const formData = new FormData()
    formData.append('data', file)

    try {
      await uploadToolCSV(formData)

      msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString(), () => {
        this.fetchTableData()
      })
    } catch (e) {
      this.$log.debug(`TOOL_COMPANY_MASTER > uploadToolCSV: e=`, e)
    }
  }

  /**
   * 入力ファイルをチェックする
   *
   * @param {File} file file
   * @returns {boolean} チェック結果
   */
  inputFileCheck(file: File): boolean {
    const fileName = file.name
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const fileExtension = fileName.split('.').pop().toLowerCase()
    if (fileExtension !== 'csv') {
      msgBoxService.info(i18n.t('gpsTracker.companyInfo.csvTypeMsg').toString())
      return false
    }

    if (file.size === 0) {
      msgBoxService.info(i18n.t('vaildate.csvFile.noData', [fileName]).toString())
      return false
    }

    if (file.size > 2 * 1024 * 1024) {
      msgBoxService.info(i18n.t('vaildate.csvFile.maxSize', [2]).toString())
      return false
    }

    return true
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.searchBox {
  display: flex;
  align-items: center;

  ::v-deep .el-input__inner {
    height: 35px;
    line-height: 35px;
    padding: 0 8px;
  }
}

.fileInput {
  display: none;
}
</style>
